import { useState } from "react";
import { Stack } from "@altimateai/lego";
import { Button, Badge } from "@altimateai/lego";
import { DeleteIcon, EditIcon } from "@assets/icons";
import InputParameterForm from "./InputParameterForm";
import { InputParameter } from "./constants";

interface ToolInputParameterListProps {
  inputs: InputParameter[];
  onChange: (inputs: InputParameter[]) => void;
  allowEdit?: boolean;
  /**
   * Controlled editing index. If provided, the parent controls which input is being edited.
   */
  editingIndex?: number | null;
  /**
   * Setter for controlled editing index. If provided, the parent controls which input is being edited.
   */
  setEditingIndex?: (idx: number | null) => void;
}

/**
 * ToolInputParameterList
 * Shows a list of input parameters with edit/delete options.
 * Handles editing inline using InputParameterForm.
 * Does NOT include an 'Add Input' button (parent should handle adding).
 *
 * If editingIndex/setEditingIndex are provided, the parent controls which input is being edited.
 */
const ToolInputParameterList = ({
  inputs,
  onChange,
  allowEdit = true,
  editingIndex: controlledEditingIndex,
  setEditingIndex: setControlledEditingIndex,
}: ToolInputParameterListProps) => {
  const [internalEditingIndex, setInternalEditingIndex] = useState<number | null>(null);
  const editingIndex =
    controlledEditingIndex !== undefined ? controlledEditingIndex : internalEditingIndex;
  const setEditingIndex = setControlledEditingIndex || setInternalEditingIndex;

  const handleEdit = (_input: InputParameter, idx: number) => {
    setEditingIndex(idx);
  };

  const handleDelete = (idx: number) => {
    onChange(inputs.filter((_, i) => i !== idx));
    if (editingIndex === idx) setEditingIndex(null);
  };

  const handleSave = (input: InputParameter) => {
    if (editingIndex !== null) {
      const updated = [...inputs];
      updated[editingIndex] = input;
      onChange(updated);
      setEditingIndex(null);
    }
  };

  const handleCancel = () => {
    setEditingIndex(null);
  };

  return (
    <Stack direction="column" className="al-border al-rounded-md al-divide-y">
      {inputs.map((input, idx) => (
        <div key={idx} className="al-p-3 hover:al-bg-muted/50">
          {editingIndex === idx ? (
            <InputParameterForm initialValue={input} onSave={handleSave} onCancel={handleCancel} />
          ) : (
            <Stack direction="row" className="al-justify-between al-items-center">
              <Stack direction="column" className="al-gap-1">
                <Stack direction="column" justify="start" align="start">
                  <div className="al-max-w-full al-overflow-hidden al-text-ellipsis al-whitespace-nowrap">
                    <span className="al-font-medium al-text-sm md:al-text-xs lg:al-text-[11px] xl:al-text-[10px]">
                      {input.name}
                    </span>
                  </div>
                  <div>
                    <Badge variant="outline" className="al-h-5">
                      {input.type}
                    </Badge>
                    {input.required && (
                      <Badge variant="outline" className="al-h-5">
                        Required
                      </Badge>
                    )}
                  </div>
                </Stack>
              </Stack>
              {allowEdit && (
                <Stack direction="row" className="al-gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Edit parameter"
                    className="al-h-7 al-w-7"
                    onClick={() => handleEdit(input, idx)}
                  >
                    <EditIcon className="al-h-4 al-w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Delete parameter"
                    className="al-h-7 al-w-7 hover:al-text-destructive"
                    onClick={() => handleDelete(idx)}
                  >
                    <DeleteIcon className="al-h-4 al-w-4" />
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </div>
      ))}
    </Stack>
  );
};

export default ToolInputParameterList;
export type { ToolInputParameterListProps };
